import React from 'react';
import {
  FileTextOutlined,
  FormOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Button } from 'antd';
import type { StoreState } from '../../data/store';
import type { FileReference } from '../extractCitedFiles';
import styles from './ReferencesCard.module.scss';

const EMPTY_FILES = {} as const;

const PREVIEW_COUNT = 10;

const NOTE_DATE_FORMAT = 'YYYY-MM-DD HH:mm';

const noteTitle = (when?: string): string =>
  when ? `Note [${dayjs(when).format(NOTE_DATE_FORMAT)}]` : 'Note';

const TYPE_RANK = { file: 0, knowledge: 1, note: 2 } as const;

interface RefGroup {
  key: string;
  name: string;
  isNote: boolean;
  fileId?: string;
  primaryPage?: number;
  knowledgePath?: string;
  knowledgeAnchor?: string;
}

const typeRank = (group: RefGroup): number =>
  group.knowledgePath
    ? TYPE_RANK.knowledge
    : group.isNote
    ? TYPE_RANK.note
    : TYPE_RANK.file;

interface Props {
  references: FileReference[];
  onFileClick: (fileId: string, page?: number) => void;
  onKnowledgeClick?: (path: string, anchor?: string) => void;
}

const ReferencesCard: React.FC<Props> = ({
  references,
  onFileClick,
  onKnowledgeClick,
}) => {
  const { dossierId } = useParams<{ dossierId: string }>();
  const dossierFiles = useSelector(
    (s: StoreState) =>
      (dossierId ? s.files.files[dossierId] : undefined) ?? EMPTY_FILES
  );
  const [expanded, setExpanded] = React.useState(false);

  const groups = React.useMemo<RefGroup[]>(
    () =>
      references
        .map((ref) => {
          const file = dossierFiles[ref.file_id];
          const isNote =
            ref.isActivity === true || file?.file_type === 'ACTIVITY';
          return {
            key: ref.file_id,
            name: isNote
              ? noteTitle(file?.created_at ?? file?.received_at)
              : file?.file_name ?? ref.file_name ?? 'File',
            isNote,
            fileId: ref.file_id,
            // Notes are single-page records; open them without a page anchor.
            primaryPage: isNote ? undefined : ref.primaryPage,
            knowledgePath: ref.knowledgePath,
            knowledgeAnchor: ref.knowledgeAnchor,
          };
        })
        .sort(
          (a, b) =>
            typeRank(a) - typeRank(b) ||
            a.name.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
        ),
    [references, dossierFiles]
  );

  if (groups.length === 0) {
    return null;
  }

  const visible = expanded ? groups : groups.slice(0, PREVIEW_COUNT);
  const hidden = groups.length - PREVIEW_COUNT;

  return (
    <aside className={styles.card}>
      <div className={styles.header}>
        <span>References</span>
        <span className={styles.count}>{groups.length}</span>
      </div>
      <div className={styles.list}>
        {visible.map((group) => {
          const typeClass = group.knowledgePath
            ? styles.typeKnowledge
            : group.isNote
            ? styles.typeNote
            : styles.typeFile;
          return (
            <div key={group.key} className={styles.item}>
              <Button
                type="text"
                size="small"
                className={styles.fileBtn}
                icon={
                  group.knowledgePath ? (
                    <ReadOutlined className={typeClass} />
                  ) : group.isNote ? (
                    <FormOutlined className={typeClass} />
                  ) : (
                    <FileTextOutlined className={typeClass} />
                  )
                }
                onClick={() =>
                  group.knowledgePath
                    ? onKnowledgeClick?.(
                        group.knowledgePath,
                        group.knowledgeAnchor
                      )
                    : onFileClick(group.fileId!, group.primaryPage)
                }
                title={group.name}
              >
                <span className={styles.fileName}>{group.name}</span>
              </Button>
            </div>
          );
        })}
      </div>
      {hidden > 0 && (
        <Button
          type="link"
          size="small"
          className={styles.toggle}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `Show ${hidden} more`}
        </Button>
      )}
    </aside>
  );
};

export default ReferencesCard;
